import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
} from '@mui/material';
import {
  Security as SecurityIcon,
  QrCode as QrCodeIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Smartphone as PhoneIcon,
} from '@mui/icons-material';
import twoFactorServices from '../../services/twoFactorServices';

const TwoFactorSettings = () => {
  const [status, setStatus] = useState({ enabled: false, hasBackupCodes: false, trustedDevicesCount: 0 });
  const [loading, setLoading] = useState(true);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [backupCodesDialogOpen, setBackupCodesDialogOpen] = useState(false);
  const [trustedDevicesDialogOpen, setTrustedDevicesDialogOpen] = useState(false);
  
  const [setupStep, setSetupStep] = useState(0);
  const [setupData, setSetupData] = useState({ secret: '', qrCode: '', manualEntryKey: '' });
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [disableCode, setDisableCode] = useState('');
  const [trustedDevices, setTrustedDevices] = useState([]);
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [actionLoading, setActionLoading] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await twoFactorServices.getStatus();
      setStatus(response.data);
    } catch (error) {
      console.error('Error loading 2FA status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleStartSetup = async () => {
    try {
      setActionLoading(true);
      const response = await twoFactorServices.setup();
      setSetupData(response.data);
      setSetupStep(0);
      setVerificationCode('');
      setSetupDialogOpen(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to start 2FA setup',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyAndEnable = async () => {
    if (verificationCode.length !== 6) {
      setSnackbar({ open: true, message: 'Please enter a 6-digit code', severity: 'error' });
      return;
    }

    try {
      setActionLoading(true);
      const response = await twoFactorServices.enable(setupData.secret, verificationCode);
      setBackupCodes(response.data.backupCodes);
      setSetupStep(2);
      loadStatus();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Invalid verification code',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable = async () => {
    if (disableCode.length < 6) {
      setSnackbar({ open: true, message: 'Please enter your verification code', severity: 'error' });
      return;
    }

    try {
      setActionLoading(true);
      await twoFactorServices.disable(disableCode);
      setDisableDialogOpen(false);
      setDisableCode('');
      setSnackbar({ open: true, message: '2FA has been disabled', severity: 'success' });
      loadStatus();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Invalid verification code',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    const code = prompt('Enter your current 2FA code to regenerate backup codes:');
    if (!code) return;

    try {
      setActionLoading(true);
      const response = await twoFactorServices.regenerateBackupCodes(code);
      setBackupCodes(response.data.backupCodes);
      setBackupCodesDialogOpen(true);
      setSnackbar({ open: true, message: 'New backup codes generated', severity: 'success' });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to regenerate backup codes',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleLoadTrustedDevices = async () => {
    try {
      const response = await twoFactorServices.getTrustedDevices();
      setTrustedDevices(response.data);
      setTrustedDevicesDialogOpen(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to load trusted devices',
        severity: 'error'
      });
    }
  };

  const handleClearTrustedDevices = async () => {
    if (!window.confirm('Are you sure you want to remove all trusted devices?')) return;

    try {
      await twoFactorServices.clearTrustedDevices();
      setTrustedDevices([]);
      setSnackbar({ open: true, message: 'All trusted devices removed', severity: 'success' });
      loadStatus();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to clear trusted devices',
        severity: 'error'
      });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copied to clipboard', severity: 'success' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <SecurityIcon color="primary" />
        <Typography variant="h6">Two-Factor Authentication</Typography>
        <Chip
          label={status.enabled ? 'Enabled' : 'Disabled'}
          color={status.enabled ? 'success' : 'default'}
          size="small"
          icon={status.enabled ? <CheckIcon /> : <WarningIcon />}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add an extra layer of security to your account by requiring a verification code from your authenticator app.
      </Typography>

      {status.enabled ? (
        <Box>
          <Alert severity="success" sx={{ mb: 2 }}>
            Two-factor authentication is enabled for your account.
          </Alert>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRegenerateBackupCodes}
              disabled={actionLoading}
            >
              Regenerate Backup Codes
            </Button>
            <Button
              variant="outlined"
              startIcon={<PhoneIcon />}
              onClick={handleLoadTrustedDevices}
            >
              Trusted Devices ({status.trustedDevicesCount})
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDisableDialogOpen(true)}
            >
              Disable 2FA
            </Button>
          </Box>

          {!status.hasBackupCodes && (
            <Alert severity="warning">
              You don't have any backup codes. Generate new ones to ensure account recovery.
            </Alert>
          )}
        </Box>
      ) : (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Protect your account with two-factor authentication using an authenticator app like Google Authenticator or Authy.
          </Alert>
          <Button
            variant="contained"
            startIcon={<QrCodeIcon />}
            onClick={handleStartSetup}
            disabled={actionLoading}
            sx={{ bgcolor: '#0D3B59' }}
          >
            Enable Two-Factor Authentication
          </Button>
        </Box>
      )}

      {/* Setup Dialog */}
      <Dialog open={setupDialogOpen} onClose={() => !actionLoading && setSetupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Stepper activeStep={setupStep} sx={{ mb: 3, mt: 1 }}>
            <Step><StepLabel>Scan QR Code</StepLabel></Step>
            <Step><StepLabel>Verify</StepLabel></Step>
            <Step><StepLabel>Backup Codes</StepLabel></Step>
          </Stepper>

          {setupStep === 0 && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </Typography>
              {setupData.qrCode && (
                <Box sx={{ mb: 2 }}>
                  <img src={setupData.qrCode} alt="QR Code" style={{ maxWidth: 200 }} />
                </Box>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Or enter this code manually:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                  {setupData.manualEntryKey}
                </Typography>
                <IconButton size="small" onClick={() => copyToClipboard(setupData.manualEntryKey)}>
                  <CopyIcon />
                </IconButton>
              </Box>
            </Box>
          )}

          {setupStep === 1 && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Enter the 6-digit code from your authenticator app to verify setup:
              </Typography>
              <TextField
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                inputProps={{ 
                  maxLength: 6, 
                  style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 } 
                }}
                sx={{ width: 200 }}
                autoFocus
              />
            </Box>
          )}

          {setupStep === 2 && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Save these backup codes!</strong> You can use them to access your account if you lose your authenticator device.
                Each code can only be used once.
              </Alert>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: 1, 
                bgcolor: '#f5f5f5', 
                p: 2, 
                borderRadius: 1,
                fontFamily: 'monospace'
              }}>
                {backupCodes.map((code, index) => (
                  <Typography key={index} variant="body1">{code}</Typography>
                ))}
              </Box>
              <Button
                startIcon={<CopyIcon />}
                onClick={() => copyToClipboard(backupCodes.join('\n'))}
                sx={{ mt: 2 }}
              >
                Copy All Codes
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {setupStep < 2 && (
            <Button onClick={() => setSetupDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
          )}
          {setupStep === 0 && (
            <Button onClick={() => setSetupStep(1)} variant="contained">
              Next
            </Button>
          )}
          {setupStep === 1 && (
            <>
              <Button onClick={() => setSetupStep(0)}>Back</Button>
              <Button 
                onClick={handleVerifyAndEnable} 
                variant="contained" 
                disabled={verificationCode.length !== 6 || actionLoading}
              >
                {actionLoading ? <CircularProgress size={20} /> : 'Verify & Enable'}
              </Button>
            </>
          )}
          {setupStep === 2 && (
            <Button onClick={() => setSetupDialogOpen(false)} variant="contained">
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={disableDialogOpen} onClose={() => setDisableDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will remove the extra security layer from your account.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your current 2FA code to confirm:
          </Typography>
          <TextField
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="Enter code"
            fullWidth
            autoFocus
            helperText="Enter 6-digit code or 8-character backup code"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDisable} color="error" variant="contained" disabled={actionLoading}>
            Disable 2FA
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={backupCodesDialogOpen} onClose={() => setBackupCodesDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Backup Codes</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Save these codes securely. Previous codes are now invalid.
          </Alert>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: 1, 
            bgcolor: '#f5f5f5', 
            p: 2, 
            borderRadius: 1,
            fontFamily: 'monospace'
          }}>
            {backupCodes.map((code, index) => (
              <Typography key={index} variant="body1">{code}</Typography>
            ))}
          </Box>
          <Button
            startIcon={<CopyIcon />}
            onClick={() => copyToClipboard(backupCodes.join('\n'))}
            sx={{ mt: 2 }}
          >
            Copy All Codes
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupCodesDialogOpen(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>

      {/* Trusted Devices Dialog */}
      <Dialog open={trustedDevicesDialogOpen} onClose={() => setTrustedDevicesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Trusted Devices</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These devices won't require 2FA verification when logging in.
          </Typography>
          {trustedDevices.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No trusted devices
            </Typography>
          ) : (
            <List>
              {trustedDevices.map((device, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={device.name}
                    secondary={`Added: ${new Date(device.createdAt).toLocaleDateString()}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearTrustedDevices} color="error" disabled={trustedDevices.length === 0}>
            Remove All
          </Button>
          <Button onClick={() => setTrustedDevicesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default TwoFactorSettings;
